import React, { useState, useRef } from 'react';
import * as MuiCore from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import Papa from 'papaparse';

const CSVUploader = ({
    onFileChange,
    onSubmit,
    validateParsedData,
    instructionLines = [],
    showPreview = true,
    maxFileSize = 10,
    showConfirmButton = true,
}) => {
    const [parsing, setParsing] = useState(false);
    const [parsedContent, setParsedContent] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const validateFile = (file) => {
        const validExtensions = ['.csv'];
        if (
            file.type !== 'text/csv' &&
            !validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
        ) {
            return 'Please select a valid CSV file';
        }
        if (file.size > maxFileSize * 1024 * 1024) {
            return `File size must be less than ${maxFileSize}MB`;
        }
        return null;
    };

    const parseCsv = (file) => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                skipEmptyLines: 'greedy',
                dynamicTyping: false,
                complete: (results) => {
                    if (!results.data.length) {
                        reject(new Error('File is empty'));
                        return;
                    }
                    const headers = results.data[0];
                    const rows = results.data.slice(1);
                    if (rows.length === 0) {
                        reject(new Error('File contains no data'));
                        return;
                    }
                    const payload = { headers, rows, totalRows: rows.length };
                    if (validateParsedData) {
                        const { error } = validateParsedData(payload);
                        if (error) {
                            reject(new Error(error));
                            return;
                        }
                    }
                    resolve(payload);
                },
            });
        });
    };

    const handleSetError = (msg) => {
        if (msg) {
            setError(msg);
            setUploadedFile(null);
            onFileChange(null);
        } else {
            setError('');
        }
    };

    const handleFile = async (file) => {
        const validationError = validateFile(file);
        if (validationError) {
            handleSetError(validationError);
            return;
        }
        handleSetError();
        setUploadedFile(file);
        setParsing(true);
        try {
            const payload = await parseCsv(file);
            setParsedContent(payload);
            handleSetError();
            onFileChange(payload);
        } catch (e) {
            handleSetError(e?.message ?? 'Error processing file. Please try again');
        } finally {
            setParsing(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files[0]) handleFile(files[0]);
    };

    const handleFileInput = (e) => {
        const files = e.target.files;
        if (files && files[0]) handleFile(files[0]);
    };

    const removeFile = () => {
        setUploadedFile(null);
        setError('');
        setParsedContent(null);
        onFileChange(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <MuiCore.Box style={{ width: '100%' }}>
            {/* Drop Zone */}
            <MuiCore.Paper
                variant="outlined"
                onClick={uploadedFile ? undefined : () => fileInputRef.current?.click()}
                onDragEnter={uploadedFile ? undefined : handleDragEnter}
                onDragLeave={uploadedFile ? undefined : handleDragLeave}
                onDragOver={uploadedFile ? undefined : handleDragOver}
                onDrop={uploadedFile ? undefined : handleDrop}
                style={{
                    border: `2px dashed ${isDragging ? '#7F2062' : '#bdbdbd'}`,
                    backgroundColor: isDragging ? 'rgba(127, 32, 98, 0.05)' : 'transparent',
                    cursor: uploadedFile ? 'default' : 'pointer',
                    padding: 24,
                    textAlign: 'center',
                    borderRadius: 8,
                    transition: 'all 0.2s ease',
                }}
            >
                {parsing ? (
                    <MuiCore.CircularProgress style={{ color: '#7F2062' }} />
                ) : uploadedFile ? (
                    <MuiCore.Box>
                        <CheckCircleIcon style={{ color: '#7F2062', fontSize: 40, marginBottom: 8 }} />
                        <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia' }}>
                            {uploadedFile.name}
                        </MuiCore.Typography>
                        <MuiCore.Typography variant="subtitle1" style={{ fontFamily: 'Sofia', color: '#666' }}>
                            {formatFileSize(uploadedFile.size)}
                        </MuiCore.Typography>
                        <MuiCore.Box style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8 }}>
                            <MuiCore.Button
                                startIcon={<DeleteIcon />}
                                variant="outlined"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); removeFile(); }}
                                style={{ fontFamily: 'Sofia', borderRadius: 100, textTransform: 'none' }}
                            >
                                Remove
                            </MuiCore.Button>
                            {parsedContent && showConfirmButton && (
                                <MuiCore.Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => onSubmit(parsedContent)}
                                    style={{ fontFamily: 'Sofia', borderRadius: 100, textTransform: 'none', backgroundColor: '#7F2062', color: '#fff' }}
                                >
                                    Confirm Selection
                                </MuiCore.Button>
                            )}
                        </MuiCore.Box>
                    </MuiCore.Box>
                ) : (
                    <MuiCore.Box>
                        <CloudUploadIcon style={{ fontSize: 48, color: isDragging ? '#7F2062' : '#757575', marginBottom: 8 }} />
                        <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia', fontWeight: 600 }}>
                            {isDragging ? 'Drop your file here' : 'Drag & drop your file here'}
                        </MuiCore.Typography>
                        {!isDragging && (
                            <MuiCore.Typography variant="body1" style={{ fontFamily: 'Sofia', color: '#666' }}>
                                or click this area
                            </MuiCore.Typography>
                        )}
                    </MuiCore.Box>
                )}
            </MuiCore.Paper>

            {/* Error Box */}
            {error && (
                <MuiCore.Box
                    style={{
                        border: '1px solid #f44336',
                        borderRadius: 8,
                        padding: 8,
                        backgroundColor: 'rgba(244, 67, 54, 0.05)',
                        marginTop: 8,
                        textAlign: 'center',
                    }}
                >
                    <MuiCore.Typography style={{ color: '#f44336', fontFamily: 'Sofia' }}>
                        {error}
                    </MuiCore.Typography>
                </MuiCore.Box>
            )}

            {/* Instructions or Preview */}
            {parsedContent && showPreview ? (
                <MuiCore.Box style={{ marginTop: 8 }}>
                    <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia', marginBottom: 4 }}>
                        File Preview:
                    </MuiCore.Typography>
                    <MuiCore.TableContainer component={MuiCore.Paper} variant="outlined">
                        <MuiCore.Table size="small">
                            <MuiCore.TableHead>
                                <MuiCore.TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                    {parsedContent.headers.map((header, i) => (
                                        <MuiCore.TableCell key={i} style={{ fontWeight: 600, fontFamily: 'Sofia' }}>
                                            {header}
                                        </MuiCore.TableCell>
                                    ))}
                                </MuiCore.TableRow>
                            </MuiCore.TableHead>
                            <MuiCore.TableBody>
                                {parsedContent.rows.map((row, i) => (
                                    <MuiCore.TableRow key={i}>
                                        {row.map((cell, j) => (
                                            <MuiCore.TableCell key={j} style={{ fontFamily: 'Sofia' }}>
                                                {cell}
                                            </MuiCore.TableCell>
                                        ))}
                                    </MuiCore.TableRow>
                                ))}
                            </MuiCore.TableBody>
                        </MuiCore.Table>
                    </MuiCore.TableContainer>
                </MuiCore.Box>
            ) : !parsedContent && (
                <MuiCore.List dense>
                    {instructionLines.map((line, i) => (
                        <MuiCore.ListItem key={i} style={{ paddingLeft: 0 }}>
                            <MuiCore.ListItemText
                                primary={<span style={{ fontFamily: 'Sofia' }}>• {line}</span>}
                            />
                        </MuiCore.ListItem>
                    ))}
                    <MuiCore.ListItem style={{ paddingLeft: 0 }}>
                        <MuiCore.ListItemText
                            primary={<span style={{ fontFamily: 'Sofia' }}>• Accepted formats: CSV (.csv)</span>}
                        />
                    </MuiCore.ListItem>
                    <MuiCore.ListItem style={{ paddingLeft: 0 }}>
                        <MuiCore.ListItemText
                            primary={<span style={{ fontFamily: 'Sofia' }}>• Maximum file size: {maxFileSize}MB</span>}
                        />
                    </MuiCore.ListItem>
                </MuiCore.List>
            )}

            {/* Hidden file input */}
            <input
                style={{ display: 'none' }}
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInput}
            />
        </MuiCore.Box>
    );
};

export default CSVUploader;
